// whiteboard.stroke-log-tail-* — live tail of the session buffer for the
// timelapse feature (絵ログ live tail — growth-timelapse-share-2026-07-02.md
// §4-§5, feature/stroke-log-live-tail).
//
// Problem this solves: a room's stroke log segments (T0) are only flushed to
// object storage when a snapshot publish trims the session buffer, and the
// snapshot cooldown is effectively 5 minutes (`SnapshotDispatcher`
// `DEFAULT_COOLDOWN_MS`). So the newest ~5 minutes of drawing lives ONLY in
// the WS server's in-memory session buffer and never reaches the R2 segments
// a timelapse downloads → "the latest strokes are missing from my timelapse".
//
// Route decision (WS, not HTTP): the session buffer is held by the **WS
// server** process (`Room.sessionBuffer`), NOT the dispatcher (which only
// presigns R2 objects for `GET /rooms/:id/stroke-log`). A client opening the
// timelapse is already WS-connected + joined to the room, so it can ask its
// own socket for the un-flushed tail. This mirrors `room.resume`'s delta mode
// (read `sessionBuffer.deltaSince`, ship the events) — a single-writer,
// in-process read with no cross-process routing. An HTTP path would need
// room→WS routing + a new WS-internal endpoint just to read state the client's
// socket already reaches.
//
// ─── How the client combines this with R2 segments ─────────────────────────
//
// The client passes `sinceSeq` = the highest seq it already has from the R2
// segment listing (= `max(segment.toSeq)`) or its snapshot seq. The server
// returns the drawing-only events with `sinceSeq < seq <= toSeq`, where
// `toSeq` is the room's current seq. The client wraps the response into a
// synthetic `StrokeLogSegment`:
//
//   { version: 1, roomId, fromSeq: sinceSeq + 1, toSeq, flushedAtMs,
//     flushReason: 'live', events }
//
// and feeds it to `mergeSegments` alongside the downloaded R2 segments.
// `flushedAtMs` is stamped `now` server-side, so it is strictly larger than
// any R2 segment's `flushedAtMs` and therefore WINS every overlapping range
// in `mergeSegments` (range-based, larger-`flushedAtMs`-wins) — the live
// buffer is the freshest truth. When `toSeq === sinceSeq` (nothing new, or a
// post-restart room whose current seq is below the client's R2 tail) the tail
// is empty and the client skips the wrap.
//
// ─── seq contiguity / dedup ────────────────────────────────────────────────
//
// The server uses `deltaSince(sinceSeq)` (NOT `deltaForSnapshot`), so it never
// re-emits the in-flight-stroke / pending-imagepaste duplicates that carry
// `seq <= sinceSeq` (those are already in R2). Within one response seq is
// strictly increasing (session buffer invariant). Combined with the
// range-based overlap resolution in `mergeSegments`, any seq shared with an
// R2 segment is de-duplicated (live tail wins), so replay sees each seq once.
import { z } from 'zod';
import { strokeLogEventSchema } from '../shared/strokeLog.js';
/**
 * `whiteboard.stroke-log-tail-request` — client → server.
 *
 * Asks the WS server for the drawing events the client does not yet have
 * from the R2 stroke log (= the un-flushed tail of the session buffer).
 * The connection must already be joined to a room; the server derives the
 * room from `connection.roomId` (same as `stroke.*`), so no roomId is carried.
 */
export const whiteboardStrokeLogTailRequestSchema = z.object({
    t: z.literal('whiteboard.stroke-log-tail-request'),
    /**
     * The highest seq the client already has (inclusive) from the R2 segment
     * listing (`max(segment.toSeq)`) or its snapshot seq. The server returns
     * only events with `seq > sinceSeq`. A value ahead of the room's current
     * seq (legitimately possible after a WS restart resets the seq counter to
     * the last snapshot) yields an empty tail rather than an error.
     */
    sinceSeq: z.number().int().nonnegative(),
});
/**
 * `whiteboard.stroke-log-tail` — server → requesting client only (unicast,
 * not a broadcast; carries no `uid`/`seq`/`ts` server-stamp — it is a
 * response, not a persisted broadcast).
 *
 * `events` reuse `strokeLogEventSchema` — the exact same drawing-only shape
 * the client already parses for downloaded R2 segments — so the client can
 * drop them straight into a synthetic `StrokeLogSegment` (see module header).
 */
export const whiteboardStrokeLogTailSchema = z.object({
    t: z.literal('whiteboard.stroke-log-tail'),
    /** Echo of the request `sinceSeq` (exclusive lower bound; lets the async client correlate). */
    sinceSeq: z.number().int().nonnegative(),
    /**
     * The room's current seq at response time (inclusive upper bound of the
     * tail). Always `>= sinceSeq` (a request with `sinceSeq` beyond the current
     * seq is clamped to it, yielding an empty tail). The client's synthetic
     * segment covers `sinceSeq + 1 .. toSeq`.
     */
    toSeq: z.number().int().nonnegative(),
    /**
     * Server `now` (epoch ms) when the tail was assembled. Used as the synthetic
     * segment's `flushedAtMs` so it wins overlap resolution against every R2
     * segment (which were flushed earlier). See module header.
     */
    flushedAtMs: z.number().int().nonnegative(),
    /** Drawing-only events with `sinceSeq < seq <= toSeq`, ascending seq. */
    events: z.array(strokeLogEventSchema),
});
//# sourceMappingURL=strokeLogTail.js.map